import { api, searchApi } from '@ardoq/api';
import {
  AdvancedSearchResultType,
  type MetaModel,
  type TraversalLoadedState,
} from '@ardoq/api-types';
import type {
  ComponentHit,
  PseudoTraversal,
  TraversalConstruction,
} from '../types';
import { traversalConstructionOperations } from '../traversalConstructionOperations';

const MAX_SEARCH_HITS = 50;

const searchComponentsByName = async (
  name: string
): Promise<ComponentHit[]> => {
  const response = api.logErrorIfNeeded(
    await searchApi.customSearch(name, {
      types: AdvancedSearchResultType.COMPONENT,
      size: MAX_SEARCH_HITS,
    })
  );
  if (!response) return [];
  return response.results.map(({ doc }) => ({
    id: doc._id,
    name: doc.name,
    typeName: doc.typeName,
  }));
};

/**
 * The real data a pseudo-traversal is constructed from: the metamodel's
 * component type names and walk steps, and the components its names find.
 * Names of start components of active loaded traversals with the same start
 * type resolve to them; every other name is searched.
 */
export const findRealData = async (
  pseudoTraversal: PseudoTraversal,
  metaModel: MetaModel,
  activeLoadedTraversals: TraversalLoadedState[]
): Promise<TraversalConstruction> => {
  const componentTypeNames =
    traversalConstructionOperations.collectComponentTypeNames(metaModel);
  const loadedHitsByNameKey =
    traversalConstructionOperations.indexLoadedStartComponentsByName(
      activeLoadedTraversals,
      pseudoTraversal.startSet.componentType
    );
  return {
    pseudoTraversal,
    componentTypeNames,
    steps: traversalConstructionOperations.indexWalkSteps(metaModel.triples),
    hitsByNameKey: new Map(
      await Promise.all(
        traversalConstructionOperations
          .collectSearchedNames(pseudoTraversal, componentTypeNames)
          .map(async name => {
            const nameKey = traversalConstructionOperations.normalizeName(name);
            return [
              nameKey,
              loadedHitsByNameKey.get(nameKey) ??
                (await searchComponentsByName(name)),
            ] as const;
          })
      )
    ),
  };
};
